import store from "@/src/store/index.js";
import { chatTransport } from "@/src/config/chatTransport.js";
import { getServiceConfig } from "@/src/config/transportFactory.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

let heartbeatInterval = null;
let visibilityHandler = null;
let beforeUnloadHandler = null;
let wasVisibleBefore = true;
const HEARTBEAT_INTERVAL_MS = 30_000;
const JITTER_MS = 5_000;

function getJitteredInterval() {
  return HEARTBEAT_INTERVAL_MS + Math.floor(Math.random() * JITTER_MS);
}

function scheduleNextHeartbeat() {
  heartbeatInterval = setTimeout(() => {
    if (document.visibilityState === "visible") {
      sendHeartbeat();
    }
    scheduleNextHeartbeat();
  }, getJitteredInterval());
}

export async function fetchBulkPresence() {
  try {
    const res = await chatTransport.get("/v1/realtime/presence/bulk");
    if (res.data?.success && res.data?.users) {
      const usersMap = res.data.users;
      const presenceMap = {};
      // Backend returns only online users as { "userId": { is_online, last_seen } }
      // Absent users are offline — mark existing presence entries as offline
      const currentPresence = store.getters["users/presence"] || {};
      for (const userId of Object.keys(currentPresence)) {
        if (!usersMap[userId]) {
          presenceMap[userId] = { is_online: false, last_seen: currentPresence[userId]?.last_seen || null };
        }
      }
      for (const [userId, data] of Object.entries(usersMap)) {
        presenceMap[userId] = {
          is_online: data.is_online,
          last_seen: data.last_seen || null,
        };
      }
      store.commit("users/SET_BULK_PRESENCE", presenceMap);
    }
  } catch (error) {
    console.error("[Presence] Failed to fetch bulk presence:", error);
  }
}

export async function sendHeartbeat() {
  try {
    await chatTransport.post("/v1/realtime/presence", {});
  } catch (error) {
    console.error("[Presence] Failed to send heartbeat:", error);
  }
}

export function sendOfflineBeacon() {
  try {
    const { baseUrl } = getServiceConfig("chat");
    const url = `${baseUrl}/v1/realtime/presence/offline`;
    const workspaceId = WorkspaceService.getCurrentWorkspaceId();
    const userId = localStorage.getItem("userId");
    if (!workspaceId || !userId) return;

    const payload = JSON.stringify({
      workspace_id: Number(workspaceId),
      user_id: Number(userId),
    });
    const blob = new Blob([payload], { type: "text/plain" });
    navigator.sendBeacon(url, blob);
  } catch {
    // Best-effort — if it fails, backend TTL handles it after 90s
  }
}

export function startHeartbeat() {
  stopHeartbeat();
  sendHeartbeat();
  scheduleNextHeartbeat();

  // Pause heartbeat when tab is hidden, resume when visible
  visibilityHandler = () => {
    const isVisible = document.visibilityState === "visible";
    if (isVisible && !wasVisibleBefore) {
      sendHeartbeat();
    }
    wasVisibleBefore = isVisible;
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  // Send offline signal on tab/browser close
  beforeUnloadHandler = () => {
    sendOfflineBeacon();
  };
  window.addEventListener("beforeunload", beforeUnloadHandler);
}

export function stopHeartbeat() {
  if (heartbeatInterval) {
    clearTimeout(heartbeatInterval);
    heartbeatInterval = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
  wasVisibleBefore = true;
}
